"use client";

import { Dialog, Box, Icon, Button, Portal } from "@chakra-ui/react";
import { useState, ReactNode } from "react";
import { TbForbidFilled } from "react-icons/tb";
import {
  SupportRequestForm,
  DemoRequestForm,
  PaymentRequestForm,
} from "./_components";

type ResponsiveHeight =
  | string
  | number
  | { base?: string | number; md?: string | number; lg?: string | number };

interface IframeDialogProps {
  iframeSrc?: string;
  title?: string;
  trigger?: ReactNode;
  iframeHeight?: ResponsiveHeight;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "full";
  placement?: "center" | "top" | "bottom";
  headerBg?: string;
  headerColor?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  variant?: "support" | "demo" | "payment";
}

const NOTION_REQUEST_1_URL = process.env.NOTION_REQUEST_1_URL || "";

export const IframeDialog = ({
  iframeSrc = `${NOTION_REQUEST_1_URL}`,
  title = "Dialog",
  trigger,
  iframeHeight = { base: "100%", md: "700px" },
  size = "md",
  placement = "center",
  headerBg = "neutral.100",
  headerColor = "neutral.900",
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  variant,
}: IframeDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = controlledOnOpenChange || setInternalOpen;
  const onDialogClose = () => setIsOpen(false);

  const cdnUrl = process.env.NEXT_PUBLIC_CLOUDFRONT_CDN_URL ?? "";

  const boxHeight =
    typeof iframeHeight === "object"
      ? iframeHeight
      : { base: iframeHeight || "100%", md: iframeHeight || "600px" };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e) => setIsOpen(e.open)}
      size={size}
      placement={placement}
    >
      {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner backdropFilter="blur(2px)">
          <Dialog.Content
            maxW={{ base: "95vw", md: "40dvw" }}
            h={{ base: "60dvh", md: "fit-content" }}
            // w="full"
            rounded="3xl"
            shadow="lg"
            id="iframe-dialog-content"
          >
            <Dialog.Header
              textStyle="sm"
              fontWeight="medium"
              color={headerColor}
              bg={headerBg}
              roundedTop="3xl"
              p={4}
            >
              <Dialog.Title>{title}</Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <Button
                  visual="none-style"
                  size="md"
                  p={0}
                  _hover={{
                    bg: "transparent",
                    transform: "rotate(90deg)",
                    shadow: "none",
                  }}
                  h={10}
                  transition="all 0.2s ease"
                >
                  <Icon as={TbForbidFilled} boxSize={6} color="red.500" />
                </Button>
              </Dialog.CloseTrigger>
            </Dialog.Header>
            <Dialog.Body
              p={{ base: 1, md: 2 }}
              pt={0}
              // bg={variant ? "white" : "black"}
              bg="neutral.100"
              roundedBottom="3xl"
              id="iframe-dialog-body"
              h="fit-content"
            >
              {variant ? (
                <Box
                  overflow="auto"
                  height={boxHeight}
                  rounded="2xl"
                  p={4}
                  bg="white"
                  h="fit-content"
                  style={{
                    backgroundImage: `url('${cdnUrl}/images/dot-grid.webp')`,
                    backgroundRepeat: "repeat",
                    backgroundSize: "200px 200px",
                    backgroundPosition: "0 0",
                  }}
                >
                  {variant === "support" ? (
                    <SupportRequestForm />
                  ) : variant === "demo" ? (
                    <DemoRequestForm onSuccess={onDialogClose} />
                  ) : (
                    <PaymentRequestForm onSuccess={onDialogClose} />
                  )}
                </Box>
              ) : (
                <Box overflow="auto" height={boxHeight} rounded="3xl">
                  <iframe
                    src={iframeSrc}
                    width="100%"
                    height="100%"
                    style={{ border: "none", display: "block" }}
                    title={title}
                    allowFullScreen
                  />
                </Box>
              )}
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
